import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { acctFieldLabel } from './typography';
import type { CompanyBalance } from './balanceUtils';

type Props = {
  debitLocal: number;
  creditLocal: number;
  debitUSD: number;
  creditUSD: number;
  diff: number;
  balanced: boolean;
  companyTotals: CompanyBalance[];
  companyNames?: Record<number, string>;
};

export const JournalBalanceBar: React.FC<Props> = ({
  debitLocal,
  creditLocal,
  debitUSD,
  creditUSD,
  diff,
  balanced,
  companyTotals,
  companyNames = {},
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const companyRows = companyTotals.filter((c) => c.debit > 0 || c.credit > 0 || c.diff !== 0);

  return (
    <div
      className={cn(
        'flex-shrink-0 border-t px-3 py-2 shadow-[0_-4px_12px_rgba(0,0,0,0.15)] sm:px-5',
        colors.border,
        balanced ? colors.success : colors.warning,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white',
              balanced ? 'bg-emerald-600' : 'bg-amber-600',
            )}
          >
            {balanced ? 'Balanced' : 'Out of balance'}
          </span>
          {!balanced && (
            <span className={cn('text-xs font-medium', isDark ? 'text-amber-200' : 'text-amber-800')}>
              Difference {diff.toFixed(2)}
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-right sm:grid-cols-3 sm:gap-5 lg:grid-cols-5 lg:gap-6">
          <div>
            <p className={cn(acctFieldLabel, 'mb-0 text-[10px]', colors.text)}>Dr local</p>
            <p className={cn('text-sm font-semibold tabular-nums leading-tight', colors.text)}>{debitLocal.toFixed(2)}</p>
          </div>
          <div>
            <p className={cn(acctFieldLabel, 'mb-0 text-[10px]', colors.text)}>Cr local</p>
            <p className={cn('text-sm font-semibold tabular-nums leading-tight', colors.text)}>{creditLocal.toFixed(2)}</p>
          </div>
          <div>
            <p className={cn(acctFieldLabel, 'mb-0 text-[10px]', colors.text)}>Dr USD</p>
            <p className={cn('text-sm font-semibold tabular-nums leading-tight', colors.text)}>{debitUSD.toFixed(2)}</p>
          </div>
          <div>
            <p className={cn(acctFieldLabel, 'mb-0 text-[10px]', colors.text)}>Cr USD</p>
            <p className={cn('text-sm font-semibold tabular-nums leading-tight', colors.text)}>{creditUSD.toFixed(2)}</p>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className={cn(acctFieldLabel, 'mb-0 text-[10px]', colors.text)}>Diff local</p>
            <p
              className={cn(
                'text-sm font-semibold tabular-nums leading-tight',
                balanced
                  ? isDark
                    ? 'text-emerald-300'
                    : 'text-emerald-700'
                  : isDark
                    ? 'text-amber-300'
                    : 'text-amber-700',
              )}
            >
              {diff.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {companyRows.length > 1 && (
        <div className={cn('mt-2 space-y-0.5 border-t pt-2 text-xs', colors.border)}>
          <p className={cn('mb-1 text-xs font-medium', colors.textMuted)}>Per company</p>
          {companyRows.map((c) => (
            <div key={c.companyId} className={cn('flex flex-wrap gap-x-4 gap-y-0.5 tabular-nums', colors.textSecondary)}>
              <span className="min-w-[8rem] font-medium">{companyNames[c.companyId] ?? `Company ${c.companyId}`}</span>
              <span>Dr {c.debit.toFixed(2)}</span>
              <span>Cr {c.credit.toFixed(2)}</span>
              <span className={c.diff === 0 ? '' : isDark ? 'text-amber-300' : 'text-amber-700'}>
                Δ {c.diff.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
